import React, { useContext, useRef, useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
	Box,
	Card,
	CardActionArea,
	CardContent,
	Typography,
	IconButton,
	CircularProgress,
	Grid,
	Autocomplete,
	TextField,
	Button,
	Tooltip,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	InputAdornment,
	Stack,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SearchIcon from "@mui/icons-material/Search";
import { ArrowBackIosNew, ArrowForwardIos } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { GlobalContext } from "../../GlobalContext";
import { obtenerDatos } from "../../api/config";
import { ModalChartHome } from "./PopUp";
import CombinedCard from "../Charts.jsx/NewCharts/chartsLogic/CombinedCard";
import LineChartMUI from "../Charts.jsx/NewCharts/chartsLogic/LineMUICharts";
import {
	obtenerTotales,
	obtenerLogrado,
	obtenerDiferencias,
	obtenerDatosTorta,
	obtenerDatosBarras,
	obtenerDatosTabla,
} from "../Charts.jsx/NewCharts/chartsLogic/datacharts";

const InfoIconButton = ({ title, placement = "left" }) => (
	<Tooltip arrow placement={placement} title={title}>
		<IconButton
			size="small"
			sx={{
				backgroundColor: "background.paper",
				border: "1px solid",
				borderColor: "divider",
				width: 24,
				height: 24,
			}}
		>
			<InfoOutlinedIcon fontSize="small" />
		</IconButton>
	</Tooltip>
);

const DAY_MAP = {
	0: "lunes",
	1: "martes",
	2: "miercoles",
	3: "jueves",
	4: "viernes",
	5: "sabado",
	6: "domingo",
};

const EMPTY_WEEK = {
	lunes: "-",
	martes: "-",
	miercoles: "-",
	jueves: "-",
	viernes: "-",
	sabado: "-",
	domingo: "-",
};

const SHIFT_SECTIONS = ["Diurno", "Nocturno", "Madrugada"];

const formatHour = (value) => {
	if (!value) return "";
	return String(value).slice(0, 5);
};

const formatPeriodsList = (periods = []) => {
	if (!Array.isArray(periods) || periods.length === 0) return "-";

	const formatted = periods
		.map((period) => {
			const start = formatHour(period?.start_time);
			const end = formatHour(period?.end_time);

			if (start && end) return `${start} - ${end}`;
			if (start) return start;
			if (end) return end;
			return null;
		})
		.filter(Boolean);

	return formatted.length > 0 ? formatted.join(" / ") : "-";
};

const normalizeContractsByType = (contracts = []) => {
	return contracts.reduce(
		(acc, contract) => {
			const tipo = contract.tipo_contrato || "Sin tipo";
			const days = { ...EMPTY_WEEK };

			const dataDays = Array.isArray(contract.contracts_per_day) ? contract.contracts_per_day : [];
			const dataPeriods = Array.isArray(contract.contracts_per_periods)
				? contract.contracts_per_periods
				: [];

			dataDays.forEach((dayItem) => {
				const dayKey = DAY_MAP[dayItem.day];
				if (!dayKey) return;

				if (dayItem.value !== 1) {
					days[dayKey] = "-";
					return;
				}

				const periodGroup = dataPeriods.find(
					(periodItem) =>
						periodItem.config_contracts_per_days_id === dayItem.config_contracts_per_days_id,
				);

				days[dayKey] = formatPeriodsList(periodGroup?.periods || []);
			});

			const row = {
				id: contract.id,
				contrato: contract.nombre || contract.nombre_contrato || contract.kronos_name || "-",
				ukg: contract.kronos_name || "-",
				...days,
			};

			if (!acc[tipo]) acc[tipo] = [];
			acc[tipo].push(row);

			return acc;
		},
		{
			Diurno: [],
			Nocturno: [],
			Madrugada: [],
		},
	);
};

const Inicio = () => {
	const { user, authenticated, site_id, viewPermissions, permissions, permissionsRol } =
		useContext(GlobalContext);

	const { t } = useTranslation();
	const [openPopup, setOpenPopup] = useState(false);
	const [loading, setLoading] = useState(false);
	const [dataByMonth, setDataByMonth] = useState(null);
	const [errorCode, setErrorCode] = useState(null);
	const scrollContainerRef = useRef(null);

	const [selectedMes, setSelectedMes] = useState(() => {
		const today = new Date();
		return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
	});

	const [homeTab, setHomeTab] = useState("resultados");
	const [siteConfig, setSiteConfig] = useState(null);
	const [contractsByType, setContractsByType] = useState({
		Diurno: [],
		Nocturno: [],
		Madrugada: [],
	});
	const [parametersLoading, setParametersLoading] = useState(false);
	const [searchContract, setSearchContract] = useState("");

	const i18n = localStorage.getItem("i18nextLng");

	useEffect(() => {
		if (!authenticated || !site_id) return;

		const fetchData = async () => {
			setLoading(true);
			try {
				const resp = await obtenerDatos(
					`/outputs/get_approved_output_ad?site_id=${site_id}&language=${i18n}`,
				);

				setErrorCode(null);
				setDataByMonth(resp || null);
			} catch (error) {
				const code = error.response?.status ?? -1;
				console.error("Error fetching approved outputs:", error);
				setErrorCode(code);
				setDataByMonth(null);
			} finally {
				setLoading(false);
			}
		};

		fetchData();
	}, [authenticated, site_id, i18n]);

	const mesesOptions = useMemo(() => {
		if (!dataByMonth) return [];
		const keys = Object.keys(dataByMonth);

		keys.sort((a, b) => {
			const [aY, aM] = a.split("-").map(Number);
			const [bY, bM] = b.split("-").map(Number);
			if (aY !== bY) return aY - bY;
			return aM - bM;
		});

		return keys.map((key) => {
			const [year, month] = key.split("-");
			const label = new Date(+year, +month - 1)
				.toLocaleString("es-CL", { month: "short", year: "2-digit" })
				.replace(".", "");
			return { value: key, label };
		});
	}, [dataByMonth]);

	useEffect(() => {
		if (!mesesOptions.length) return;
		const existe = mesesOptions.some((opt) => opt.value === selectedMes);
		if (!existe) setSelectedMes(mesesOptions[0].value);
	}, [mesesOptions, selectedMes]);

	const selectedMesOption = useMemo(
		() => mesesOptions.find((opt) => opt.value === selectedMes) || null,
		[mesesOptions, selectedMes],
	);

	const mesesSeleccionadosFilter = useMemo(
		() => (selectedMesOption ? [Number(selectedMesOption.value.split("-")[1])] : []),
		[selectedMesOption],
	);

	const mesesSeleccionadosNums = useMemo(() => (selectedMes ? [selectedMes] : []), [selectedMes]);

	const [idealMonthData, realMonthData] = useMemo(() => {
		const raw = dataByMonth?.[selectedMes];
		if (!raw) return [{}, {}];
		const monthArr = Array.isArray(raw) ? raw : [raw];
		return [monthArr[0] || {}, monthArr[1] || {}];
	}, [dataByMonth, selectedMes]);

	const safeIdealMonthData = useMemo(() => {
		return {
			...idealMonthData,
			output_planning_monthly: Array.isArray(idealMonthData.output_planning_monthly)
				? idealMonthData.output_planning_monthly
				: [],
			contrato_monthly: {
				Diurno: idealMonthData.contrato_monthly?.Diurno ?? [],
				Nocturno: idealMonthData.contrato_monthly?.Nocturno ?? [],
				Madrugada: idealMonthData.contrato_monthly?.Madrugada ?? [],
			},
			porcentaje_cobertura: idealMonthData.porcentaje_cobertura ?? 0,
			porcentaje_holgura: idealMonthData.porcentaje_holgura ?? 0,
		};
	}, [idealMonthData]);

	const resumenMes = useMemo(() => safeIdealMonthData.contrato_monthly, [safeIdealMonthData]);

	const safeIdeal = useMemo(
		() => ({
			...idealMonthData,
			output_planning_monthly: Array.isArray(idealMonthData.output_planning_monthly)
				? idealMonthData.output_planning_monthly
				: [],
			contrato_monthly: {
				Diurno: idealMonthData.contrato_monthly?.Diurno ?? [],
				Nocturno: idealMonthData.contrato_monthly?.Nocturno ?? [],
				Madrugada: idealMonthData.contrato_monthly?.Madrugada ?? [],
			},
			porcentaje_cobertura: idealMonthData.porcentaje_cobertura ?? 0,
			porcentaje_holgura: idealMonthData.porcentaje_holgura ?? 0,
		}),
		[idealMonthData],
	);

	const safeReal = useMemo(
		() => ({
			...realMonthData,
			output_planning_monthly: Array.isArray(realMonthData.output_planning_monthly)
				? realMonthData.output_planning_monthly
				: [],
			contrato_monthly: {
				Diurno: realMonthData.contrato_monthly?.Diurno ?? [],
				Nocturno: realMonthData.contrato_monthly?.Nocturno ?? [],
				Madrugada: realMonthData.contrato_monthly?.Madrugada ?? [],
			},
			porcentaje_cobertura: realMonthData.porcentaje_cobertura ?? 0,
			porcentaje_holgura: realMonthData.porcentaje_holgura ?? 0,
		}),
		[realMonthData],
	);

	const mesIdealArray = useMemo(() => [safeIdealMonthData], [safeIdealMonthData]);

	const unifiedEntry = useMemo(() => {
		if (!dataByMonth) return null;

		const e = {
			output_planning_monthly: [],
			contrato_monthly: { Diurno: [], Nocturno: [], Madrugada: [] },
			porcentaje_cobertura: [],
			porcentaje_holgura: [],
		};

		Object.values(dataByMonth).forEach((monthValue) => {
			const ideal = Array.isArray(monthValue) ? monthValue[0] || {} : monthValue;

			e.output_planning_monthly.push(...(ideal.output_planning_monthly || []));

			["Diurno", "Nocturno", "Madrugada"].forEach((sec) => {
				e.contrato_monthly[sec].push(...(ideal.contrato_monthly?.[sec] || []));
			});

			e.porcentaje_cobertura.push(ideal.porcentaje_cobertura || 0);
			e.porcentaje_holgura.push(ideal.porcentaje_holgura || 0);
		});

		return [e];
	}, [dataByMonth]);

	const { totalSP: targetTotalSP } = useMemo(
		() => obtenerTotales([safeIdeal], mesesSeleccionadosFilter),
		[safeIdeal, mesesSeleccionadosFilter],
	);

	const { totalNE: realTotalNE } = useMemo(
		() => obtenerTotales([safeReal], mesesSeleccionadosFilter),
		[safeReal, mesesSeleccionadosFilter],
	);

	const coberturaTarget = safeIdeal.porcentaje_cobertura;
	const holguraTarget = safeIdeal.porcentaje_holgura;
	const coberturaReal = safeReal.porcentaje_cobertura;
	const holguraReal = safeReal.porcentaje_holgura;

	const { datosTortaNE: datosTortaNE_Mes, datosTortaSP: datosTortaSP_Mes } = useMemo(() => {
		return obtenerDatosTorta(mesIdealArray, mesesSeleccionadosNums);
	}, [mesIdealArray, mesesSeleccionadosNums]);

	const barrasPorSeccionMes = useMemo(() => {
		return obtenerDatosBarras(mesIdealArray, mesesSeleccionadosNums);
	}, [mesIdealArray, mesesSeleccionadosNums]);

	const filasTablaMes = useMemo(() => {
		return obtenerDatosTabla(mesIdealArray, mesesSeleccionadosNums);
	}, [mesIdealArray, mesesSeleccionadosNums]);

	const diferenciasContratos = useMemo(
		() => obtenerDiferencias(mesIdealArray, mesesSeleccionadosNums),
		[mesIdealArray, mesesSeleccionadosNums],
	);

	const logradosMes = useMemo(
		() => obtenerLogrado(mesIdealArray, mesesSeleccionadosNums),
		[mesIdealArray, mesesSeleccionadosNums],
	);

	const hasTarget = Boolean(Object.keys(idealMonthData).length);
	const hasReal = Boolean(Object.keys(realMonthData).length);

	const handleScrollLeft = () =>
		scrollContainerRef.current?.scrollBy({ left: -200, behavior: "smooth" });

	const handleScrollRight = () =>
		scrollContainerRef.current?.scrollBy({ left: 200, behavior: "smooth" });

	const sections = useMemo(() => {
		const raw = [
			{
				title: t("home.section.configuration.title"),
				description: t("home.section.configuration.description"),
				subSections: [
					{ title: t("home.section.configuration.site_config.title"), route: "/config/configeditsite" },
					{ title: t("home.section.configuration.contract_config.title"), route: "/config/configedit" },
				],
			},
			{
				title: t("home.section.sim_masive.title"),
				description: t("home.section.sim_masive.description"),
				route: "/execute",
			},
			{
				title: t("home.section.review.title"),
				description: t("home.section.review.description"),
				route: "/review",
			},
			{
				title: t("home.section.simulation.title"),
				description: t("home.section.simulation.description"),
				route: "/simulation",
			},
			{
				title: t("home.section.audit.title"),
				description: t("home.section.audit.description"),
				route: "/audit",
			},
			{
				title: t("home.section.upload.title"),
				description: t("home.section.upload.description"),
				route: "/upload",
			},
		];

		return raw
			.filter((sec) => {
				if (!sec.route) return !!viewPermissions?.config;

				switch (sec.route) {
					case "/execute":
						return !!viewPermissions?.execute;
					case "/review":
						return !!viewPermissions?.review;
					case "/simulation":
						return !!viewPermissions?.simulation;
					case "/audit":
						return !!viewPermissions?.audit;
					case "/upload":
						return !!viewPermissions?.upload;
					default:
						return false;
				}
			})
			.slice(0, 4);
	}, [t, viewPermissions]);

	const hasAnySection = sections.length > 0;
	const showReplacementTabs = !hasAnySection;

	const normalizedOutputs = useMemo(() => {
		if (!dataByMonth) return [];

		const idealOutput = {};
		const realOutput = {};

		Object.entries(dataByMonth).forEach(([ym, arr]) => {
			const month = String(Number(ym.split("-")[1]));
			const ideal = Array.isArray(arr) ? arr[0] || {} : arr;
			const real = Array.isArray(arr) ? arr[1] || {} : {};

			idealOutput[month] = {
				personas_activas: ideal.personas_activas || {},
				demanda: ideal.demanda || {},
			};

			realOutput[month] = {
				personas_activas: real.personas_activas || {},
				demanda: {},
			};
		});

		return [idealOutput, realOutput];
	}, [dataByMonth]);

	const isEmptyData = useMemo(() => {
		if (!dataByMonth) return false;
		if (Array.isArray(dataByMonth)) return dataByMonth.length === 0;
		return Object.keys(dataByMonth).length === 0;
	}, [dataByMonth]);

	useEffect(() => {
		if (!authenticated || !site_id || !showReplacementTabs) return;

		const fetchParametersData = async () => {
			setParametersLoading(true);
			try {
				const [siteResp, contractsResp] = await Promise.all([
					obtenerDatos(`/config/get-config/${site_id}`),
					obtenerDatos(`/contracts/get/${site_id}`),
				]);

				setSiteConfig(siteResp || null);
				setContractsByType(normalizeContractsByType(contractsResp || []));
			} catch (error) {
				console.error("Error obteniendo datos de parámetros:", error);
				setSiteConfig(null);
				setContractsByType({
					Diurno: [],
					Nocturno: [],
					Madrugada: [],
				});
			} finally {
				setParametersLoading(false);
			}
		};

		fetchParametersData();
	}, [authenticated, site_id, showReplacementTabs]);

	const filteredContractsByType = useMemo(() => {
		const search = searchContract.trim().toLowerCase();

		if (!search) return contractsByType;

		const filterRows = (rows = []) =>
			rows.filter((row) => {
				const contrato = String(row.contrato || "").toLowerCase();
				const ukg = String(row.ukg || "").toLowerCase();
				return contrato.includes(search) || ukg.includes(search);
			});

		return {
			Diurno: filterRows(contractsByType.Diurno),
			Nocturno: filterRows(contractsByType.Nocturno),
			Madrugada: filterRows(contractsByType.Madrugada),
		};
	}, [contractsByType, searchContract]);

	const permissionsLoading =
		authenticated &&
		(permissionsRol == null || (Array.isArray(permissions) && permissions.length === 0));

	const shiftLabelMap = useMemo(
		() => ({
			Diurno: t("shifts.diurno"),
			Nocturno: t("shifts.nocturno"),
			Madrugada: t("shifts.madrugada"),
		}),
		[t],
	);

	if (permissionsLoading) {
		return (
			<Box
				sx={{
					minHeight: "80vh",
					width: "100%",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 2 }}>
			<Box sx={{ textAlign: "left", mb: 3, width: "80%" }}>
				<Typography variant="h5" sx={{ fontWeight: 700 }}>
					{t("aux.welcome")} {user}
				</Typography>
			</Box>

			{hasAnySection && (
				<Box sx={{ display: "flex", alignItems: "center", width: "80%", mb: 2, gap: 2 }}>
					<IconButton onClick={handleScrollLeft}>
						<ArrowBackIosNew />
					</IconButton>

					<Box
						ref={scrollContainerRef}
						sx={{
							display: "flex",
							flexWrap: "nowrap",
							gap: 1,
							overflowX: "hidden",
							scrollBehavior: "smooth",
							flex: 1,
						}}
					>
						{sections.map((section, index) => {
							if (section.subSections && section.subSections.length > 0) {
								return (
									<Card
										key={index}
										variant="outlined"
										sx={{
											flex: "0 0 auto",
											minWidth: 280,
											borderRadius: "20px",
											border: "1px solid",
											borderColor: "divider",
										}}
									>
										<CardContent>
											<Typography variant="h6" gutterBottom>
												{section.title}
											</Typography>
											<Typography variant="body2" color="text.secondary" gutterBottom>
												{section.description}
											</Typography>

											<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
												{section.subSections.map((subSection, subIndex) => (
													<Button
														sx={{ width: "48%" }}
														key={subIndex}
														variant="outlined"
														component={RouterLink}
														to={subSection.route}
														size="small"
													>
														{subSection.title}
													</Button>
												))}
											</Box>
										</CardContent>
									</Card>
								);
							}

							return (
								<Card
									key={index}
									variant="outlined"
									sx={{
										flex: "0 0 auto",
										minWidth: 280,
										borderRadius: "20px",
										border: "1px solid",
										borderColor: "divider",
									}}
								>
									<CardActionArea component={RouterLink} to={section.route}>
										<CardContent>
											<Typography variant="h6">{section.title}</Typography>
											<Typography variant="body2" color="text.secondary">
												{section.description}
											</Typography>
										</CardContent>
									</CardActionArea>
								</Card>
							);
						})}
					</Box>

					<IconButton onClick={handleScrollRight}>
						<ArrowForwardIos />
					</IconButton>
				</Box>
			)}

			{showReplacementTabs ? (
				<Box sx={{ width: "80%" }}>
					<Box sx={{ display: "flex", justifyContent: "flex-start", mb: 3 }}>
						<Box
							sx={{
								display: "inline-flex",
								alignItems: "center",
								p: "4px",
								borderRadius: "999px",
								bgcolor: "background.paper",
								border: "1px solid",
								borderColor: "divider",
								gap: "4px",
							}}
						>
							<Button
								onClick={() => setHomeTab("resultados")}
								variant={homeTab === "resultados" ? "contained" : "text"}
								data-tone={homeTab === "resultados" ? "dark" : undefined}
								sx={{
									minWidth: 150,
									height: 40,
									...(homeTab !== "resultados" && {
										bgcolor: "transparent",
									}),
								}}
							>
								{t("home.results.title")}
							</Button>

							<Button
								onClick={() => setHomeTab("parametros")}
								variant={homeTab === "parametros" ? "contained" : "text"}
								data-tone={homeTab === "parametros" ? "dark" : undefined}
								sx={{
									minWidth: 150,
									height: 40,
									...(homeTab !== "parametros" && {
										bgcolor: "transparent",
									}),
								}}
							>
								{t("home.tabs.parameters")}
							</Button>
						</Box>
					</Box>

					{homeTab === "resultados" && (
						<Box
							sx={{
								width: "100%",
								position: "relative",
								borderRadius: "20px",
								border: "1px solid",
								borderColor: "divider",
								p: 3,
								bgcolor: "background.paper",
							}}
						>
							<Box sx={{ textAlign: "center", mb: 1 }}>
								<Typography variant="h6" component="div">
									{t("home.results.title")}
								</Typography>
							</Box>

							<Box mt={2} mb={2}>
								<Autocomplete
									size="small"
									options={mesesOptions}
									value={selectedMesOption}
									onChange={(_, opt) => setSelectedMes(opt?.value)}
									getOptionLabel={(opt) => opt.label}
									isOptionEqualToValue={(a, b) => a.value === b.value}
									renderInput={(p) => <TextField {...p} label={t("aux.filter_per_month")} />}
								/>
							</Box>

							{loading ? (
								<Box
									mt={2}
									mb={2}
									sx={{
										width: "100%",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
									}}
								>
									<CircularProgress />
								</Box>
							) : errorCode ? (
								<Typography align="center" color="error" sx={{ py: 4 }}>
									{errorCode >= 500 ? t("home.results.error_server") : t("home.results.error_generic")}
								</Typography>
							) : isEmptyData ? (
								<Typography align="center" color="text.secondary" sx={{ py: 4 }}>
									{t("home.results.no_data")}
								</Typography>
							) : (
								<Grid
									container
									spacing={2}
									justifyContent={hasTarget && hasReal ? "flex-start" : "center"}
									mb={2}
								>
									<Grid item xs={12}>
										<Box
											sx={{
												position: "relative",
												p: 2,
											}}
										>
											<Box sx={{ position: "absolute", top: 12, right: 12, zIndex: 2 }}>
												<InfoIconButton
													placement="left"
													title={
														<Box>
															<Typography variant="body2">
																<Box component="span" sx={{ fontWeight: 700 }}>
																	{t("new_charts.combinedCardTooltip.staffingTitle")}
																</Box>{" "}
																{t("new_charts.combinedCardTooltip.staffingDescription")}
															</Typography>

															<Typography variant="body2">
																<Box component="span" sx={{ fontWeight: 700 }}>
																	{t("new_charts.combinedCardTooltip.coverageTitle")}
																</Box>{" "}
																{t("new_charts.combinedCardTooltip.coverageDescription")}
															</Typography>

															<Typography variant="body2">
																<Box component="span" sx={{ fontWeight: 700 }}>
																	{t("new_charts.combinedCardTooltip.spaceTitle")}
																</Box>{" "}
																{t("new_charts.combinedCardTooltip.spaceDescription")}
															</Typography>
														</Box>
													}
												/>
											</Box>

											<Grid container spacing={2} sx={{ pt: 1 }}>
												{hasReal && (
													<Grid item xs={hasTarget && hasReal ? 6 : 12}>
														<CombinedCard
															title={t("new_charts.real_staffing")}
															dotacion={realTotalNE}
															cobertura={coberturaReal}
															holgura={holguraReal}
															t={t}
														/>
													</Grid>
												)}

												{hasTarget && (
													<Grid item xs={hasTarget && hasReal ? 6 : 12}>
														<CombinedCard
															title={t("new_charts.target_staffing")}
															dotacion={targetTotalSP}
															cobertura={coberturaTarget}
															holgura={holguraTarget}
															t={t}
														/>
													</Grid>
												)}
											</Grid>
										</Box>
									</Grid>

									<Grid item xs={12}>
										<Card variant="outlined" sx={{ borderRadius: "20px", borderColor: "divider", mb: 1 }}>
											<LineChartMUI data={normalizedOutputs} selectedMonths={mesesSeleccionadosNums} />
										</Card>
									</Grid>

									<Grid item xs={12}>
										<Box
											sx={{
												display: "flex",
												justifyContent: "center",
												mt: 1,
												mb: 1,
												width: "100%",
											}}
										>
											<Button
												variant="outlined"
												onClick={() => setOpenPopup(true)}
												sx={{
													padding: "6px 24px",
												}}
											>
												{t("home.results.button.more")}
											</Button>
										</Box>
									</Grid>
								</Grid>
							)}
						</Box>
					)}

					{homeTab === "parametros" && (
						<Box
							sx={{
								borderRadius: "20px",
								border: "1px solid",
								borderColor: "divider",
								overflow: "hidden",
								bgcolor: "background.paper",
							}}
						>
							<Box
								sx={{
									bgcolor: "primary.dark",
									color: "common.white",
									px: 2,
									py: 1.5,
								}}
							>
								{parametersLoading ? (
									<Box
										sx={{
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											minHeight: 96,
										}}
									>
										<CircularProgress size={28} sx={{ color: "common.white" }} />
									</Box>
								) : (
									<Grid container spacing={2} alignItems="center">
										<Grid item xs={12} md={6}>
											<Stack spacing={0.5}>
												<Box sx={{ display: "flex", gap: 1 }}>
													<Typography sx={{ opacity: 0.9, fontSize: "0.95rem" }}>
														{t("home.parameters.site_name")}:
													</Typography>
													<Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
														{siteConfig?.nombre || "-"}
													</Typography>
												</Box>

												<Box sx={{ display: "flex", gap: 1 }}>
													<Typography sx={{ opacity: 0.9, fontSize: "0.95rem" }}>
														{t("lab_tool.site.labels.cu").toUpperCase()}:
													</Typography>
													<Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
														{siteConfig?.cu ?? "-"}
													</Typography>
												</Box>
											</Stack>
										</Grid>

										<Grid item xs={12} md={6}>
											<Box sx={{ display: "flex", justifyContent: { xs: "stretch", md: "flex-end" } }}>
												<TextField
													size="small"
													placeholder={t("aux.search")}
													value={searchContract}
													onChange={(e) => setSearchContract(e.target.value)}
													sx={{
														width: { xs: "100%", md: 280 },
														"& .MuiOutlinedInput-root": {
															height: 44,
															color: "common.white",
															backgroundColor: "transparent",
															"& .MuiOutlinedInput-notchedOutline": {
																borderColor: "rgba(255,255,255,0.5)",
															},
															"&:hover .MuiOutlinedInput-notchedOutline": {
																borderColor: "rgba(255,255,255,0.8)",
															},
														},
														"& input::placeholder": {
															color: "rgba(255,255,255,0.8)",
															opacity: 1,
														},
														"& .MuiInputAdornment-root svg": {
															color: "rgba(255,255,255,0.8)",
														},
													}}
													InputProps={{
														startAdornment: (
															<InputAdornment position="start">
																<SearchIcon fontSize="small" />
															</InputAdornment>
														),
													}}
												/>
											</Box>
										</Grid>
									</Grid>
								)}
							</Box>

							{!parametersLoading && (
								<Box sx={{ bgcolor: "background.paper" }}>
									{SHIFT_SECTIONS.map((section, index) => (
										<Accordion
											key={section}
											defaultExpanded={index === 0}
											disableGutters
											sx={{
												boxShadow: "none",
												borderTop: index === 0 ? "none" : "1px solid",
												borderColor: "divider",
												"&:before": {
													display: "none",
												},
											}}
										>
											<AccordionSummary
												expandIcon={<ExpandMoreIcon sx={{ color: "text.primary" }} />}
												sx={{
													minHeight: 60,
													px: 3,
													"& .MuiAccordionSummary-content": {
														my: 0,
													},
												}}
											>
												<Typography sx={{ fontWeight: 700 }}>{shiftLabelMap[section]}</Typography>
											</AccordionSummary>

											<AccordionDetails sx={{ p: 0 }}>
												<TableContainer
													component={Paper}
													elevation={0}
													sx={{
														borderTop: "1px solid",
														borderColor: "divider",
														borderRadius: 0,
														"& .MuiTableCell-root": {
															borderBottom: "1px solid",
															borderColor: "divider",
															verticalAlign: "top",
														},
														"& .MuiTableHead-root": {
															backgroundColor: "grey.100",
														},
														"& .MuiTableHead-root .MuiTableCell-root": {
															fontWeight: 700,
															color: "text.primary",
														},
													}}
												>
													<Table size="small">
														<TableHead>
															<TableRow>
																<TableCell>{t("lab_tool.contract.labels.nombre")}</TableCell>
																<TableCell>{t("lab_tool.contract.labels.kronos_name")}</TableCell>
																<TableCell>{t("aux.days.lunes")}</TableCell>
																<TableCell>{t("aux.days.martes")}</TableCell>
																<TableCell>{t("aux.days.miercoles")}</TableCell>
																<TableCell>{t("aux.days.jueves")}</TableCell>
																<TableCell>{t("aux.days.viernes")}</TableCell>
																<TableCell>{t("aux.days.sabado")}</TableCell>
																<TableCell>{t("aux.days.domingo")}</TableCell>
															</TableRow>
														</TableHead>

														<TableBody>
															{filteredContractsByType[section]?.length > 0 ? (
																filteredContractsByType[section].map((row) => (
																	<TableRow key={row.id}>
																		<TableCell sx={{ fontWeight: 600 }}>{row.contrato}</TableCell>
																		<TableCell>{row.ukg}</TableCell>
																		<TableCell>{row.lunes}</TableCell>
																		<TableCell>{row.martes}</TableCell>
																		<TableCell>{row.miercoles}</TableCell>
																		<TableCell>{row.jueves}</TableCell>
																		<TableCell>{row.viernes}</TableCell>
																		<TableCell>{row.sabado}</TableCell>
																		<TableCell>{row.domingo}</TableCell>
																	</TableRow>
																))
															) : (
																<TableRow>
																	<TableCell colSpan={9} align="center" sx={{ py: 4 }}>
																		{t("new_charts.no_data")}
																	</TableCell>
																</TableRow>
															)}
														</TableBody>
													</Table>
												</TableContainer>
											</AccordionDetails>
										</Accordion>
									))}
								</Box>
							)}
						</Box>
					)}
				</Box>
			) : (
				<>
					<Box sx={{ textAlign: "center", mb: 1 }}>
						<Typography variant="h6" component="div">
							{t("home.results.title")}
						</Typography>
					</Box>

					<Box
						sx={{
							width: "80%",
							position: "relative",
							borderRadius: "20px",
							border: "1px solid",
							borderColor: "divider",
							p: 3,
							bgcolor: "background.paper",
						}}
					>
						<Box mt={2} mb={2}>
							<Autocomplete
								size="small"
								options={mesesOptions}
								value={selectedMesOption}
								onChange={(_, opt) => setSelectedMes(opt?.value)}
								getOptionLabel={(opt) => opt.label}
								isOptionEqualToValue={(a, b) => a.value === b.value}
								renderInput={(p) => <TextField {...p} label={t("aux.filter_per_month")} />}
							/>
						</Box>

						{loading ? (
							<Box
								mt={2}
								mb={2}
								sx={{
									width: "100%",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<CircularProgress />
							</Box>
						) : errorCode ? (
							<Typography align="center" color="error" sx={{ py: 4 }}>
								{errorCode >= 500 ? t("home.results.error_server") : t("home.results.error_generic")}
							</Typography>
						) : isEmptyData ? (
							<Typography align="center" color="text.secondary" sx={{ py: 4 }}>
								{t("home.results.no_data")}
							</Typography>
						) : (
							<Grid
								container
								spacing={2}
								justifyContent={hasTarget && hasReal ? "flex-start" : "center"}
								mb={2}
							>
								<Grid item xs={12}>
									<Box
										sx={{
											position: "relative",
											p: 2,
										}}
									>
										<Box sx={{ position: "absolute", top: 12, right: 12, zIndex: 2 }}>
											<InfoIconButton
												placement="left"
												title={
													<Box>
														<Typography variant="body2">
															<Box component="span" sx={{ fontWeight: 700 }}>
																{t("new_charts.combinedCardTooltip.staffingTitle")}
															</Box>{" "}
															{t("new_charts.combinedCardTooltip.staffingDescription")}
														</Typography>

														<Typography variant="body2">
															<Box component="span" sx={{ fontWeight: 700 }}>
																{t("new_charts.combinedCardTooltip.coverageTitle")}
															</Box>{" "}
															{t("new_charts.combinedCardTooltip.coverageDescription")}
														</Typography>

														<Typography variant="body2">
															<Box component="span" sx={{ fontWeight: 700 }}>
																{t("new_charts.combinedCardTooltip.spaceTitle")}
															</Box>{" "}
															{t("new_charts.combinedCardTooltip.spaceDescription")}
														</Typography>
													</Box>
												}
											/>
										</Box>

										<Grid container spacing={2} sx={{ pt: 1 }}>
											{hasReal && (
												<Grid item xs={hasTarget && hasReal ? 6 : 12}>
													<CombinedCard
														title={t("new_charts.real_staffing")}
														dotacion={realTotalNE}
														cobertura={coberturaReal}
														holgura={holguraReal}
														t={t}
													/>
												</Grid>
											)}

											{hasTarget && (
												<Grid item xs={hasTarget && hasReal ? 6 : 12}>
													<CombinedCard
														title={t("new_charts.target_staffing")}
														dotacion={targetTotalSP}
														cobertura={coberturaTarget}
														holgura={holguraTarget}
														t={t}
													/>
												</Grid>
											)}
										</Grid>
									</Box>
								</Grid>

								<Grid item xs={12}>
									<Card variant="outlined" sx={{ borderRadius: "20px", borderColor: "divider", mb: 1 }}>
										<LineChartMUI data={normalizedOutputs} selectedMonths={mesesSeleccionadosNums} />
									</Card>
								</Grid>

								<Grid item xs={12}>
									<Box
										sx={{
											display: "flex",
											justifyContent: "center",
											mt: 1,
											mb: 1,
											width: "100%",
										}}
									>
										<Button variant="outlined" onClick={() => setOpenPopup(true)}>
											{t("home.results.button.more")}
										</Button>
									</Box>
								</Grid>
							</Grid>
						)}
					</Box>
				</>
			)}

			<ModalChartHome
				open={openPopup}
				onClose={() => setOpenPopup(false)}
				monthName={selectedMes}
				loading={loading}
				data={{
					mensual: unifiedEntry,
					lineal: normalizedOutputs,
					mesesSeleccionados: mesesSeleccionadosNums,
				}}
				totalNE={realTotalNE}
				totalSP={targetTotalSP}
				coberturaReal={coberturaReal}
				holguraReal={holguraReal}
				coberturaIdeal={coberturaTarget}
				holguraIdeal={holguraTarget}
				pieData={{ datosTortaNE: datosTortaNE_Mes, datosTortaSP: datosTortaSP_Mes }}
				barData={barrasPorSeccionMes}
				tableData={filasTablaMes}
				hasIdeal={hasTarget}
				hasReal={hasReal}
				diferenciasContratos={diferenciasContratos}
				diferenciasTabla={{ diferencias: diferenciasContratos, logrados: logradosMes }}
				summaryData={resumenMes}
			/>
		</Box>
	);
};

export { Inicio };
